import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Button,
  Fade,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
import { api } from "../services/api";
import AppBackground from "../components/layout/AppBackground";

const ProfileEdit = () => {
  const { t, i18n } = useTranslation("profile");
  const theme = useTheme();
  const { token, isAuthenticated } = useAuth();
  const firstNameRef = useRef();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    profile_name: "",
    email: "",
    gender: "u",
    language_preference: i18n.language?.startsWith("es") ? "es" : "en",
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        // });
        // const data = await res.json();
        // if (res.ok) {
        const res = await api.get("/auth/profile");
        const data = res.data;
        if (res.status === 200) {
          setForm({
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            profile_name: data.profile_name || "",
            email: data.email || "",
            gender: data.gender || "m",
            language_preference: data.language_preference || "en",
          });
          setTimeout(() => {
            firstNameRef.current?.focus();
          }, 0);
        }
      } catch (err) {
        setError(t("fetch_error"));
      }
    };

    fetchProfile();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!form.first_name.trim()) {
      setError(t("required_fields_error"));
      document.querySelector("input[name='first_name']")?.focus();
      return;
    }
    if (!form.last_name.trim()) {
      setError(t("required_fields_error"));
      document.querySelector("input[name='last_name']")?.focus();
      return;
    }
    if (!form.profile_name.trim()) {
      setError(t("required_fields_error"));
      document.querySelector("input[name='profile_name']")?.focus();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (res.ok) {
        updateUser(form); // 🔄 actualiza datos en frontend
        navigate("/"); // ✅ redirige al home directamente
      } else {
        setError(data.msg || t("update_failed"));
        setLoading(false); // 🔁 re-habilita el botón si hubo error
      }
    } catch (err) {
      setError(t("network_error"));
      setLoading(false); // 🔁 también en caso de error de red
    }
  };

  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "#212121",
            color: "#fff",
            borderRadius: 4,
            p: 4,
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
            boxShadow: "0 0 20px rgba(0,0,0,0.5)",
          }}
        >
          <img
            src="/assets/logo-icon.png"
            alt="Pitchest Logo"
            style={{ height: 80, marginBottom: 16 }}
          />
          <Typography variant="h5" gutterBottom>
            {t("profile_title")}
          </Typography>

          <TextField
            label={t("first_name")}
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            inputRef={firstNameRef}
            fullWidth
            margin="normal"
            size="small"
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("last_name")}
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("profile_name")}
            name="profile_name"
            value={form.profile_name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("email")}
            name="email"
            type="email"
            value={form.email}
            disabled
            fullWidth
            margin="normal"
            size="small"
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: "#aaa !important", // color visible para el label
              },
            }}
            InputProps={{
              sx: {
                color: "#aaa", // color del texto (valor del campo)
                backgroundColor: "#2a2a2a", // fondo para diferenciar
                "& input.Mui-disabled": {
                  WebkitTextFillColor: "#bbb", // asegura color en Chrome
                },
              },
            }}
          />

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel
              id="gender-label"
              shrink
              sx={{
                color: "#eee",
                mb: 0.5, // 👈 subimos el label para evitar solapamiento
              }}
            >
              {t("gender")}
            </InputLabel>
            <Select
              labelId="gender-label"
              id="gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              label={t("gender")}
              sx={{
                color: "#fff",
                ".MuiSelect-icon": { color: "#454346" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#454346" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#A855F7",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#fff", // fondo del menú desplegable
                    color: "#222", // color del texto
                    "& .MuiMenuItem-root": {
                      fontWeight: 500,
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#eee",
                      color: "#555",
                    },
                    "& .Mui-selected:hover": {
                      backgroundColor: "#ddd",
                    },
                  },
                },
              }}
          >
              <MenuItem value="m">{t("gender_m")}</MenuItem>
              <MenuItem value="f">{t("gender_f")}</MenuItem>
              <MenuItem value="u">{t("gender_u")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel
              id="language-label"
              sx={{ color: "#eee" }}
            >
              {t("language_preference")}
            </InputLabel>
            <Select
              labelId="language-label"
              name="language_preference"
              value={form.language_preference}
              onChange={handleChange}
              label={t("language_preference")} // 👈 importante para que el label se posicione bien
              sx={{
                color: "#fff",
                ".MuiSelect-icon": { color: "#454346" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#454346" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#A855F7",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#222",
                    "& .MuiMenuItem-root": {
                      fontWeight: 500,
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#eee",
                      color: "#555",
                    },
                    "& .Mui-selected:hover": {
                      backgroundColor: "#ddd",
                    },
                  },
                },
              }}
            >
              <MenuItem value="en">{t("english")}</MenuItem>
              <MenuItem value="es">{t("spanish")}</MenuItem>
            </Select>
          </FormControl>

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          {successMsg && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
              {successMsg}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ mt: 2, borderRadius: 999, py: 1.2 }}
          >
            {loading ? t("saving") : t("update_button")}
          </Button>
        </Box>
      </AppBackground>
    </Fade>
  );
};

export default ProfileEdit;
